/* ==========================================================================
   Virsaay — opening loader and hero

   The rule this file exists to honour: the loader image and the hero image are
   the SAME DOM element. Nothing is swapped, cloned, cross-faded or hidden.
   `[data-hero-media]` is measured in its final hero position, transformed back
   to a small centred state before the first paint the visitor sees, and then
   released — so the expansion is literally the same pixels growing into place.

   The loader itself is deliberately minimal and quick: the house logo over a
   plain canvas. The logo is drawn at the header logo's rendered size, so when
   it travels into the header it only translates — it never scales.
   ========================================================================== */

(function () {
  'use strict';

  var Motion = window.VirsaayMotion;
  var settings = (window.VirsaayTheme && window.VirsaayTheme.loader) || {};
  var START_SCALE = typeof settings.startScale === 'number' ? settings.startScale : 0.3;
  // Merchant "logo hold": how long the logo rests before the page opens.
  var HOLD = typeof settings.duration === 'number' ? Math.min(Math.max(settings.duration, 0), 2) : 0.4;
  // A slow hero image never holds the visitor for longer than this.
  var IMAGE_WAIT_CAP = 1500;

  var root = document.documentElement;

  // The inline script in theme.liquid arms a timer that force-releases the page
  // if this file never runs. Once we are driving, that net is no longer needed.
  function clearFailsafe() {
    if (window.VirsaayLoaderFailsafe) {
      clearTimeout(window.VirsaayLoaderFailsafe);
      window.VirsaayLoaderFailsafe = null;
    }
  }

  function markSeen() {
    clearFailsafe();
    try {
      sessionStorage.setItem('virsaay:loader-seen', '1');
    } catch (error) {
      // Storage blocked — the loader simply plays again next navigation.
    }
  }

  /* ----------------------------------------------------------------------
     Hero behaviours that run whether or not the loader played
     ---------------------------------------------------------------------- */

  function initHeroExtras(gsap) {
    var hero = document.querySelector('[data-hero]');
    if (!hero) return;

    // Scroll cue nudges away as soon as the visitor engages.
    var cue = hero.querySelector('[data-hero-cue]');
    if (cue && !Motion.reduced) {
      var cueTween = gsap.to(cue, {
        opacity: 0,
        y: 18,
        ease: 'none',
        scrollTrigger: {
          trigger: hero,
          start: 'top top',
          end: '+=240',
          scrub: true
        }
      });
      Motion.register(hero, cueTween.scrollTrigger);
    }

    // A slow drift on the hero image as the page leaves it behind.
    var media = hero.querySelector('[data-hero-media] .virsaay-hero__media-inner');
    if (media && !Motion.reduced && Motion.parallaxEnabled) {
      var driftTween = gsap.fromTo(
        media,
        { yPercent: 0, scale: 1 },
        {
          yPercent: 8,
          scale: 1.06,
          ease: 'none',
          scrollTrigger: {
            trigger: hero,
            start: 'top top',
            end: 'bottom top',
            scrub: true
          }
        }
      );
      Motion.register(hero, driftTween.scrollTrigger);
    }

    // Marquee loop.
    //
    // This is an infinite tween, so left alone it burns a compositor frame
    // every frame forever — including while the hero is scrolled far off
    // screen and while the tab is in the background. It is gated on both:
    // a ScrollTrigger toggles it with viewport visibility, and a
    // visibilitychange handler pauses it with the tab.
    var marqueeTrack = hero.querySelector('.virsaay-hero__marquee-track');
    if (marqueeTrack && !Motion.reduced) {
      var ST = window.ScrollTrigger || null;
      var loop = gsap.to(marqueeTrack, {
        xPercent: -50,
        duration: 26,
        ease: 'none',
        repeat: -1
      });

      var inView = true;
      var tabVisible = !document.hidden;
      var sync = function () {
        if (inView && tabVisible) loop.play();
        else loop.pause();
      };

      var marqueeTrigger = null;
      if (ST) {
        marqueeTrigger = ST.create({
          trigger: marqueeTrack,
          start: 'top bottom',
          end: 'bottom top',
          onToggle: function (self) {
            inView = self.isActive;
            sync();
          }
        });
      }

      var onVisibility = function () {
        tabVisible = !document.hidden;
        sync();
      };
      document.addEventListener('visibilitychange', onVisibility);
      sync();

      Motion.register(hero, {
        kill: function () {
          document.removeEventListener('visibilitychange', onVisibility);
          if (marqueeTrigger) marqueeTrigger.kill();
          loop.kill();
        }
      });
    }
  }

  /* ----------------------------------------------------------------------
     Loader
     ---------------------------------------------------------------------- */

  function run(gsap) {
    var loader = document.getElementById('VirsaayLoader');
    var media = document.querySelector('[data-hero-media]');

    // Nothing to animate into — release the page and get out of the way.
    if (!loader || !media) {
      root.classList.remove('virsaay-loading');
      if (loader) loader.style.display = 'none';
      markSeen();
      Motion.emit('loader:done', { skipped: true });
      return;
    }

    var backdrop = loader.querySelector('.virsaay-loader__backdrop');
    var wordmark = loader.querySelector('[data-loader-wordmark]');
    var loaderLogo = wordmark ? wordmark.querySelector('img') : null;

    var header = document.querySelector('[data-header]');
    var headerLogo = header ? header.querySelector('[data-logo] img') : null;
    var heroCopy = document.querySelector('[data-hero-copy]');
    var heroCue = document.querySelector('[data-hero-cue]');
    var copyChildren = [];
    if (heroCopy) {
      var found = heroCopy.querySelectorAll('[data-hero-el], [data-hero-heading]');
      copyChildren = found.length ? Array.from(found) : Array.from(heroCopy.children);
    }

    // Keep the overlay painted even after the html class comes off.
    loader.style.display = 'block';

    // The elements the loader owns are hidden inline so removing the html class
    // later cannot make them pop in.
    if (header) gsap.set(header, { opacity: 0 });
    if (headerLogo) gsap.set(headerLogo, { opacity: 0 });
    if (wordmark) gsap.set(wordmark, { opacity: 0 });
    if (heroCopy) gsap.set(heroCopy, { opacity: 1, zIndex: 9995 });
    if (copyChildren.length) gsap.set(copyChildren, { opacity: 0, y: 40 });
    if (heroCue) gsap.set(heroCue, { opacity: 0, y: 16 });

    /* -- Measure ------------------------------------------------------- */

    // The loader logo takes the header logo's exact rendered box. Both images
    // use object-fit: contain, so equal boxes draw identical pixels.
    function syncLogoSize() {
      if (!loaderLogo || !headerLogo) return;
      var rect = headerLogo.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        loaderLogo.style.width = rect.width + 'px';
        loaderLogo.style.height = rect.height + 'px';
      }
    }

    var state = null;

    function measure() {
      // Temporarily clear transform to read natural hero rect
      var prevTransform = media.style.transform;
      media.style.transform = 'none';
      var rect = media.getBoundingClientRect();
      media.style.transform = prevTransform;

      state = {
        x: window.innerWidth / 2 - (rect.left + rect.width / 2),
        y: window.innerHeight / 2 - (rect.top + rect.height / 2),
        scale: START_SCALE,
        width: rect.width,
        height: rect.height,
        wordmarkY: 0
      };
      return state;
    }

    function applyStart() {
      syncLogoSize();
      measure();
      gsap.set(media, {
        x: state.x,
        y: state.y,
        scale: state.scale,
        opacity: 1,
        transformOrigin: '50% 50%',
        force3D: true
      });

      // The logo sits a short, proportional gap above the small hero image.
      if (wordmark) {
        var gap = Math.max(20, Math.min(48, window.innerHeight * 0.04));
        var markHeight = wordmark.getBoundingClientRect().height || 42;
        state.wordmarkY = -((state.height * state.scale) / 2 + gap + markHeight / 2);
        gsap.set(wordmark, { xPercent: -50, yPercent: -50, x: 0, y: state.wordmarkY });
      }
    }

    var positioned = window.getComputedStyle(media).position !== 'static';
    if (!positioned) media.style.position = 'relative';
    media.style.willChange = 'transform';

    applyStart();

    var settled = false;
    var onResize = function () {
      if (settled) return;
      applyStart();
    };
    window.addEventListener('resize', onResize);

    /* -- Intro: the logo settles in ------------------------------------ */

    var introDur = Motion.dur(0.45);
    if (wordmark) {
      gsap.to(wordmark, { opacity: 1, duration: introDur, ease: 'power2.out' });
    }

    /* -- Release once the hold has passed and the hero is decoded ------- */

    var heroImage = document.querySelector('[data-hero-image]');

    function imageReady() {
      return new Promise(function (resolve) {
        if (!heroImage || (heroImage.tagName && heroImage.tagName.toLowerCase() === 'svg')) {
          resolve();
          return;
        }

        var done = false;
        var finish = function () {
          if (done) return;
          done = true;
          resolve();
        };

        setTimeout(finish, IMAGE_WAIT_CAP);

        if (heroImage.complete && heroImage.naturalWidth > 0) {
          if (heroImage.decode) heroImage.decode().then(finish, finish);
          else finish();
          return;
        }

        heroImage.addEventListener('load', finish, { once: true });
        heroImage.addEventListener('error', finish, { once: true });
      });
    }

    function wait(seconds) {
      return new Promise(function (resolve) {
        setTimeout(resolve, seconds * 1000);
      });
    }

    Promise.all([wait(Math.max(introDur, HOLD)), imageReady()]).then(outro);

    /* -- Outro: logo docks in the header, hero grows into the page ------ */

    function outro() {
      settled = true;
      window.removeEventListener('resize', onResize);

      var tl = gsap.timeline({
        onComplete: function () {
          // The loader logo now sits exactly on the header logo, so swapping
          // one for the other is invisible.
          if (headerLogo) gsap.set(headerLogo, { clearProps: 'opacity' });
          if (loader) loader.style.display = 'none';
          root.classList.remove('virsaay-loading');

          gsap.set(media, { clearProps: 'transform,boxShadow' });
          if (!positioned) media.style.removeProperty('position');
          media.style.removeProperty('z-index');
          media.style.removeProperty('will-change');

          if (header) gsap.set(header, { clearProps: 'opacity' });
          if (heroCopy) gsap.set(heroCopy, { clearProps: 'opacity,zIndex' });
          if (copyChildren.length) gsap.set(copyChildren, { clearProps: 'transform,opacity' });
          if (heroCue) gsap.set(heroCue, { clearProps: 'transform,opacity' });

          markSeen();
          Motion.emit('loader:done', { skipped: false });
          Motion.refresh(0);
        }
      });

      // 1. The logo glides into the header — translation only, never scale.
      if (wordmark) {
        var target = headerLogo ? headerLogo.getBoundingClientRect() : null;
        if (target && target.width > 0) {
          var mark = wordmark.getBoundingClientRect();
          var dx = target.left + target.width / 2 - (mark.left + mark.width / 2);
          var dy = target.top + target.height / 2 - (mark.top + mark.height / 2);
          tl.to(
            wordmark,
            {
              x: dx,
              y: state.wordmarkY + dy,
              duration: Motion.dur(0.95),
              ease: 'power3.inOut'
            },
            0.05
          );
        } else {
          tl.to(wordmark, { opacity: 0, duration: Motion.dur(0.4), ease: 'power2.in' }, 0);
        }
      }

      // 2. The same hero element grows from the centre into place.
      tl.to(
        media,
        {
          x: 0,
          y: 0,
          scale: 1,
          boxShadow: '0 0 0 rgba(0, 0, 0, 0)',
          duration: Motion.dur(1.05),
          ease: 'power3.inOut'
        },
        0
      );

      // 3. The canvas dissolves behind it.
      if (backdrop) {
        tl.to(backdrop, { opacity: 0, duration: Motion.dur(0.6), ease: 'power2.inOut' }, 0.3);
      }

      // 4. Header chrome, then the hero copy.
      if (header) {
        tl.fromTo(header, { opacity: 0 }, { opacity: 1, duration: Motion.dur(0.5), ease: 'power2.out' }, 0.55);
      }

      if (copyChildren.length) {
        tl.fromTo(
          copyChildren,
          { opacity: 0, y: 40 },
          { opacity: 1, y: 0, duration: Motion.dur(0.9), ease: 'power3.out', stagger: 0.08 },
          0.6
        );
      }

      if (heroCue) {
        tl.fromTo(
          heroCue,
          { opacity: 0, y: 16 },
          { opacity: 1, y: 0, duration: Motion.dur(0.6), ease: 'power2.out' },
          0.95
        );
      }
    }
  }

  /* ----------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */

  function waitForMotion(callback) {
    if (window.VirsaayMotion && window.VirsaayMotion.ready) {
      window.VirsaayMotion.ready(callback);
      return;
    }
    var attempts = 0;
    var timer = setInterval(function () {
      attempts++;
      if (window.VirsaayMotion && window.VirsaayMotion.ready) {
        clearInterval(timer);
        window.VirsaayMotion.ready(callback);
      } else if (attempts > 60) {
        clearInterval(timer);
        clearFailsafe();
        root.classList.remove('virsaay-loading');
        var loader = document.getElementById('VirsaayLoader');
        if (loader) loader.style.display = 'none';
      }
    }, 20);
  }

  waitForMotion(function (gsap) {
    Motion = window.VirsaayMotion;
    var armed = window.VirsaayLoaderArmed === true && !Motion.reduced && !!gsap;

    if (!armed) {
      clearFailsafe();
      root.classList.remove('virsaay-loading');
      var loader = document.getElementById('VirsaayLoader');
      if (loader) loader.style.display = 'none';
      var media = document.querySelector('[data-hero-media]');
      if (media) {
        media.style.removeProperty('transform');
        media.style.removeProperty('opacity');
        media.style.removeProperty('z-index');
      }
      if (gsap) initHeroExtras(gsap);
      return;
    }

    run(gsap);

    Motion.on('loader:done', function () {
      initHeroExtras(gsap);
    });
  });
})();
